<script setup>
import { defineProps, computed, reactive } from 'vue';
import Message from './Message.vue';
import EmailThreadAccordion from './EmailThreadAccordion.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import { emitter } from 'shared/helpers/mitt';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

const currentChat = useMapGetter('unifiedView/getEffectiveChat');
// Na aba "Todas as conversas" (unified view) o ReplyBox nao tem onde
// direcionar uma resposta de thread especifica de email — esconde o
// botao "Responder esta thread" dos cards pra nao confundir.
const isAllTabActive = useMapGetter('unifiedView/isAllTabActive');

// Normaliza assunto pra usar como chave de agrupamento. Mesma logica do
// backend (Emails::ThreadIdResolver.normalize_subject): tira "Re:"/"Fwd:"
// e o prefixo "[#123]" que o ConversationReplyMailer injeta por padrao.
const normalizeSubject = s =>
  String(s || '')
    .replace(/^\s*(re|fwd?|enc|res)\s*:\s*/i, '')
    .replace(/^\s*\[#?\d+\]\s*/i, '')
    .trim()
    .toLowerCase();

// Agrupa mensagens da MESMA thread pra renderizar sanfonas em vez de
// baloes soltos. Aplica so quando inbox eh email.
//
// Estrategia (em ordem de prioridade):
//   1. subject normalizado — junta emails que tem o mesmo assunto na
//      mesma conversa (cobre bounces do mailer-daemon e o caso de
//      outgoings antigos com subject identico mas Message-IDs distintos)
//   2. thread_id (Message-ID da raiz da cadeia References) — usado
//      quando o subject eh vazio.
//   3. solo-<id> — quando nem subject nem thread_id existem.
const groupedItems = computed(() => {
  if (!props.isAnEmailChannel) return null;

  const items = [];
  const threadMap = new Map();

  allMessages.value.forEach(msg => {
    if (msg.isSeparator) {
      items.push({ kind: 'separator', msg });
      return;
    }
    // Activities (atribuiu/encaminhou/etc), templates de sistema e
    // mensagens privadas NAO entram em sanfonas — sao items soltos na
    // timeline. message_type: 0=incoming, 1=outgoing, 2=activity, 3=template.
    const isRealEmail =
      (msg.messageType === MESSAGE_TYPES.INCOMING ||
        msg.messageType === MESSAGE_TYPES.OUTGOING) &&
      !msg.private;
    if (!isRealEmail) {
      // Filtra mensagens sem nada renderizavel (Message.vue retorna
      // shouldRenderMessage=false e o <li> fica vazio aparecendo como
      // "linha fininha" na UI). Mantem activities mesmo sem content
      // (eles sao renderizados via ActivityBubble com content que vem
      // do backend descrevendo a acao).
      const hasContent = msg.content || (msg.attachments?.length || 0) > 0;
      const isActivity = msg.messageType === MESSAGE_TYPES.ACTIVITY;
      if (!hasContent && !isActivity) return;
      items.push({ kind: 'looseMessage', msg });
      return;
    }
    const ca = msg.contentAttributes?.email || {};
    const rawSubject = ca.subject || '';
    const normalized = normalizeSubject(rawSubject);
    const tid = ca.threadId || ca.thread_id;

    // Chave de agrupamento — prioriza subject normalizado.
    const groupKey = normalized || tid || `solo-${msg.id}`;

    if (!threadMap.has(groupKey)) {
      const entry = {
        kind: 'thread',
        threadId: tid || groupKey,
        subject: rawSubject,
        messages: [],
        lastActivity: 0,
      };
      threadMap.set(groupKey, entry);
      items.push(entry);
    }
    const entry = threadMap.get(groupKey);
    entry.messages.push(msg);
    const ts = msg.createdAt || msg.created_at || 0;
    if (ts > entry.lastActivity) entry.lastActivity = ts;
    if (!entry.subject && rawSubject) entry.subject = rawSubject;
  });

  // Ordena tudo (threads + items soltos + separators) por timestamp.
  const sorted = items.sort((a, b) => {
    const ta = a.kind === 'thread' ? a.lastActivity : a.msg.createdAt || 0;
    const tb = b.kind === 'thread' ? b.lastActivity : b.msg.createdAt || 0;
    return ta - tb;
  });

  // Marca a ULTIMA thread como `isLast` — o card vai dar scrollIntoView
  // pra ela aparecer em foco quando o agente abrir a conversa.
  const lastThreadIdx = sorted
    .map((i, idx) => (i.kind === 'thread' ? idx : -1))
    .filter(i => i >= 0)
    .pop();
  if (lastThreadIdx !== undefined) sorted[lastThreadIdx].isLast = true;
  return sorted;
});

const onReplyThread = ({ threadId, subject }) => {
  emitter.emit('email-thread:reply', {
    threadId,
    subject,
    conversationId: currentChat.value?.id,
  });
};

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
/**
 * Formats a unix timestamp for channel separator display
 */
const formatSeparatorTime = createdAt => {
  if (!createdAt) return '';
  const date = new Date(createdAt * 1000);
  const day = date.getDate();
  const months = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
  ];
  const month = months[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month}, ${hours}:${minutes}`;
};

const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  // Don't group across channel separators
  if (current.isSeparator || next.isSeparator) return false;

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-5 py-4 bg-n-stone-25">
    <slot name="beforeAll" />

    <!-- Inbox de email: renderiza threads como sanfonas -->
    <template v-if="isAnEmailChannel && groupedItems">
      <template
        v-for="(item, idx) in groupedItems"
        :key="
          item.kind === 'thread'
            ? `t-${item.threadId}`
            : item.kind === 'looseMessage'
              ? `l-${item.msg.id}`
              : `s-${idx}`
        "
      >
        <li
          v-if="item.kind === 'separator'"
          class="flex items-center gap-2 py-2 my-1"
        >
          <div class="flex-1 border-t border-n-weak" />
          <div
            class="flex items-center gap-1.5 px-3 py-1 rounded-full bg-n-surface-2 border border-n-weak text-xs font-medium text-n-slate-11"
          >
            <span class="size-1.5 rounded-full flex-shrink-0 bg-n-brand" />
            <span :class="item.msg.icon" class="size-3 flex-shrink-0" />
            <span>{{ item.msg.inboxName }}</span>
          </div>
          <div class="flex-1 border-t border-n-weak" />
        </li>
        <Message
          v-else-if="item.kind === 'looseMessage'"
          v-bind="item.msg"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(item.msg)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          data-clarity-mask="True"
          @retry="emit('retry', item.msg)"
        />
        <EmailThreadAccordion
          v-else
          :thread-id="item.threadId"
          :messages="item.messages"
          :subject="item.subject"
          :is-last="!!item.isLast"
          :show-reply-button="!isAllTabActive"
          @reply-thread="onReplyThread"
        />
      </template>
      <slot name="after" />
    </template>

    <!-- Demais canais: fluxo original (envolto em div pra evitar warning Vue v-if+v-for) -->
    <div v-if="!(isAnEmailChannel && groupedItems)" class="contents">
      <template v-for="(message, index) in allMessages" :key="message.id">
        <!-- Channel separator marker for merged timeline -->
        <li
          v-if="message.isSeparator"
          class="flex items-center gap-2 py-2 my-1"
        >
          <div class="flex-1 border-t border-n-weak" />
          <div
            class="flex items-center gap-1.5 px-3 py-1 rounded-full bg-n-surface-2 border border-n-weak text-xs font-medium text-n-slate-11"
          >
            <span
              class="size-1.5 rounded-full flex-shrink-0"
              :class="message.canReply ? 'bg-n-brand' : 'bg-n-ruby-9'"
            />
            <span :class="message.icon" class="size-3 flex-shrink-0" />
            <span>{{ message.inboxName }}</span>
            <span class="text-n-slate-9">·</span>
            <span class="text-n-slate-9">{{
              formatSeparatorTime(message.createdAt)
            }}</span>
          </div>
          <div class="flex-1 border-t border-n-weak" />
        </li>
        <template v-else>
          <slot
            v-if="firstUnreadId && message.id === firstUnreadId"
            name="unreadBadge"
          />
          <Message
            v-bind="message"
            :is-email-inbox="isAnEmailChannel"
            :in-reply-to="getInReplyToMessage(message)"
            :group-with-next="shouldGroupWithNext(index, allMessages)"
            :inbox-supports-reply-to="inboxSupportsReplyTo"
            :current-user-id="currentUserId"
            data-clarity-mask="True"
            @retry="emit('retry', message)"
          />
        </template>
      </template>
      <slot name="after" />
    </div>
  </ul>
</template>
